import { Box } from "@mui/material";
import LoadingIndicator from "@/shared/ui/LoadingIndicator";
import { RouteReadyMarker } from "@/app/ui/RouteProgress";
import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import useCurrentTime from "@/hook/useCurrentTime";
import { isKillInitiationOpen } from "@/features/kill-initiation";
import NotFoundPage from "./NotFoundPage";
import RouteErrorBoundary from "./RouteErrorBoundary";

// Lazy load all page components for better performance
const Associate = lazy(() => import("../../features/associate/page"));
const Bank = lazy(() => import("../../features/bank/page"));
const BackfireSettings = lazy(() => import("../../features/backfire-settings/page"));
const Bodyguard = lazy(() => import("../../features/bodyguard/page"));
const Booze = lazy(() => import("../../features/booze/page"));
const BulletFactory = lazy(() => import("../../features/bullet-factory/page"));
const BulletHistory = lazy(() => import("../../features/bullet-history/page"));
const CashHistory = lazy(() => import("../../features/cash-history/page"));
const CrateOpening = lazy(() => import("../../features/crate-opening/page"));
const CreditHistory = lazy(() => import("../../features/credit-history/page"));
const Crime = lazy(() => import("../../features/crime/page"));
const CarCrusher = lazy(() => import("../../features/car-crusher/page"));
const Safehouse = lazy(() => import("../../features/safehouse/page"));
const DetectiveAgency = lazy(() => import("../../features/detective-agency/page"));
const Equipment = lazy(() => import("../../features/equipment/page"));
const EquipmentV2 = lazy(() => import("../../features/equipment/v2-page"));
const Exchange = lazy(() => import("../../features/exchange/page"));
const Family = lazy(() => import("../../features/family/page"));
const FamilyCreatePage = lazy(() => import("../../features/family/create-page"));
const FamilyLeaderBoard = lazy(() => import("../../features/family/leaderboard-page"));
const Garage = lazy(() => import("../../features/garage/page"));
const Graveyard = lazy(() => import("../../features/graveyard/page"));
const GICreditHistory = lazy(() => import("../../features/gi-credit-history/page"));
const HealthHistory = lazy(() => import("../../features/health-history/page"));
const HelperBot = lazy(() => import("../../features/helper-bot/page"));
const Hospital = lazy(() => import("../../features/hospital/page"));
const Inbox = lazy(() => import("../../features/inbox/page"));
const InventoryAirdrop = lazy(
  () => import("../../features/inventory-airdrop/page")
);
const InJailPage = lazy(() => import("../../features/jail/in-jail-page"));
const Jackpot = lazy(() => import("../../features/jackpot/page"));
const LotteryHall = lazy(() => import("../../features/lottery-hall/page"));
const Jail = lazy(() => import("../../features/jail/page"));
const KillSkill = lazy(() => import("../../features/kill-skill/page"));
const KillInitiation = lazy(() => import("../../features/kill-initiation/page"));
const KillStart = lazy(() => import("../../features/kill-initiation/start-page"));
const KillOutcomeBattle = lazy(
  () => import("../../features/kill-outcome-battle/page")
);
const Rebirth = lazy(() => import("../../features/rebirth/page"));
const UnstakeMafia = lazy(() => import("../../features/unstake-mafia/page"));
const BuyPage = lazy(() => import("../../features/landing/page"));
const Map = lazy(() => import("../../features/map/page"));
const MapGame = lazy(() => import("../../features/map/game-page"));
const MarketPage = lazy(() => import("../../features/market/page"));
const MarketingDao = lazy(() => import("../../features/marketing-dao/page"));
const Metrics = lazy(() => import("../../features/metrics/page"));
const MysteryBox = lazy(() => import("../../features/mystery-box/page"));
const Narcotics = lazy(() => import("../../features/narcotics/page"));
const NewLanding = lazy(() => import("../../features/landing/new-page"));
const NickCar = lazy(() => import("../../features/nick-car/page"));
const OrganizedCrime = lazy(() => import("../../features/organized-crime/page"));
const OrganizedCrimeList = lazy(() => import("../../features/organized-crime/list-page"));
const Perks = lazy(() => import("../../features/perks/page"));
const Premium = lazy(() => import("../../features/premium/page"));
const Profile = lazy(() => import("../../features/profile/page"));
const ProfileCreation = lazy(() => import("../../features/profile-unboxing/pages/ProfileUnboxingPage"));
const RankActivation = lazy(() => import("../../features/rank-activation/page"));
const Racing = lazy(() => import("../../features/race/page"));
const RaceResultPage = lazy(() => import("../../features/race/pages/RaceResultPage"));
const Referral = lazy(() => import("../../features/referral/page"));
const Roulette = lazy(() => import("../../features/roulette/page"));
const Shop = lazy(() => import("../../features/shop/page"));
const Slot = lazy(() => import("../../features/slot/page"));
const Story = lazy(() => import("../../features/story/page"));
const Travel = lazy(() => import("../../features/travel/page"));
const Vault = lazy(() => import("../../features/vault/page"));
const VaultCountdown = lazy(() => import("../../features/vault/countdown-page"));
const XPMarket = lazy(() => import("../../features/xp-market/page"));
const FoundersTableShares = lazy(
  () => import("../../features/founders-table-shares/page")
);
const AttemptedKills = lazy(() => import("../../features/attempted-kills/page"));

// Loading fallback component for lazy-loaded routes
const LoadingFallback = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "50vh",
    }}
  >
    <LoadingIndicator size="large" label="Loading page" />
  </Box>
);

const KillRoute = ({ startPage }: { startPage: boolean }) => {
  const currentTime = useCurrentTime();
  const isOpen = isKillInitiationOpen(currentTime * 1000);

  if (startPage) {
    return isOpen ? <Navigate to="/kill-initiation" replace /> : <KillStart currentTime={currentTime} />;
  }

  return isOpen ? <KillInitiation /> : <Navigate to="/kill-start" replace />;
};

const AppRouter = () => (
  <RouteErrorBoundary>
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route path="/" element={<NewLanding />} />
        <Route path="/premium" element={<Premium />} />
        <Route path="/buy" element={<BuyPage />} />
        <Route path="/profile" element={<NewLanding />} />
        <Route path="/profile/:profileName" element={<Profile />} />
        <Route path="/create-profile" element={<ProfileCreation />} />
        <Route path="/profile-unboxing" element={<Navigate to="/create-profile" replace />} />
        <Route path="/vault" element={<Vault />} />
        <Route path="/vault/countdown" element={<VaultCountdown />} />
        <Route path="/metrics" element={<Metrics />} />

        <Route path="/family/" element={<FamilyLeaderBoard />} />
        <Route path="/familycreate" element={<FamilyCreatePage />} />
        <Route path="/family/:familyName" element={<Family />} />

        <Route path="/crate" element={<CrateOpening />} />

        <Route path="/market" element={<MarketPage />} />
        <Route path="/market/itemList" element={<MarketPage />} />
        <Route path="/market/history" element={<MarketPage />} />
        <Route path="/market/myListings" element={<MarketPage />} />
        <Route path="/market/item/:itemId" element={<MarketPage />} />

        <Route path="/shop" element={<Shop />} />

        <Route
          path="/exchange/convert"
          element={<Exchange subpage="convert" />}
        />
        <Route path="/exchange" element={<Exchange subpage="deposit" />} />
        <Route
          path="/exchange/deposit"
          element={<Exchange subpage="deposit" />}
        />
        <Route
          path="/exchange/withdraw"
          element={<Exchange subpage="withdraw" />}
        />
        <Route
          path="/exchange/trade"
          element={<Exchange subpage="otcTrade" />}
        />
        <Route
          path="/exchange/trade-create"
          element={<Exchange subpage="otcCreate" />}
        />
        <Route
          path="/exchange/keys"
          element={<Exchange subpage="keys" />}
        />

        <Route
          path="/exchange/perkBox"
          element={<Exchange subpage="perkBox" />}
        />
        <Route
          path="/exchange/bullets"
          element={<Exchange subpage="bullets" />}
        />

        <Route path="/map" element={<Map />} />

        <Route path="/map/:city" element={<MapGame />} />

        <Route path="/dao" element={<MarketingDao />} />

        <Route path="/cash/history" element={<CashHistory />} />

        <Route path="/credit/history" element={<CreditHistory />} />

        <Route path="/roulette" element={<Roulette />} />

        <Route path="/slotmachine" element={<Slot />} />

        {/* <Route path="/businesses" element={<Business />} /> */}

        <Route path="/crimes" element={<Crime />} />

        <Route path="/jail" element={<Jail />} />

        <Route path="/travel" element={<Travel />} />

        <Route path="/nickcar" element={<NickCar />} />

        <Route path="/injail" element={<InJailPage />} />

        <Route path="/helper" element={<HelperBot />} />

        <Route path="/inbox" element={<Inbox />} />

        <Route path="/inbox/sent" element={<Inbox />} />

        <Route path="/inbox/sent/:messageId" element={<Inbox />} />

        <Route path="/inbox/new" element={<Inbox />} />

        <Route path="/inbox/:messageId" element={<Inbox />} />

        <Route path="/tools/inventory-airdrop" element={<InventoryAirdrop />} />

        <Route path="/booze" element={<Booze />} />

        <Route path="/narcotics" element={<Narcotics />} />

        <Route path="/garage" element={<Garage />} />

        <Route path="/rank" element={<Associate />} />

        <Route path="/rank-activation" element={<RankActivation />} />

        <Route path="/killskill" element={<KillSkill />} />
        <Route
          path="/kill-initiation"
          element={<KillRoute startPage={false} />}
        />
        <Route
          path="/kill-start"
          element={<KillRoute startPage />}
        />
        <Route path="/kill-outcome" element={<KillOutcomeBattle />} />
        <Route path="/kill-outcome/:killId" element={<KillOutcomeBattle />} />
        <Route path="/backfire-settings" element={<BackfireSettings />} />
        <Route path="/graveyard" element={<Graveyard />} />
        <Route path="/attempted-kills" element={<AttemptedKills />} />
        <Route path="/rebirth" element={<Rebirth />} />

        <Route path="/hospital" element={<Hospital />} />

        <Route path="/health/history" element={<HealthHistory />} />

        <Route path="/bullet/factory" element={<BulletFactory />} />

        <Route path="/bullet/history" element={<BulletHistory />} />

        <Route path="/bank" element={<Bank />} />

        <Route path="/worth" element={<Equipment />} />
        <Route path="/equipment" element={<EquipmentV2 />} />
        <Route path="/unstake-mafia" element={<UnstakeMafia />} />

        <Route path="/organized-crime" element={<OrganizedCrimeList />} />
        <Route
          path="/organized-crime/:partyId"
          element={<OrganizedCrime />}
        />

        <Route path="/xpmarket/all" element={<XPMarket type="all" />} />
        <Route path="/xpmarket/my" element={<XPMarket type="my" />} />
        <Route
          path="/xpmarket/history"
          element={<XPMarket type="history" />}
        />
        <Route path="/fts" element={<FoundersTableShares />} />

        <Route path="/racing" element={<Racing />} />
        <Route path="/racing/result/:raceId" element={<RaceResultPage />} />

        <Route path="/perks" element={<Perks />} />

        <Route path="/jackpot" element={<Jackpot />} />

        <Route path="/lottery-hall" element={<LotteryHall />} />

        <Route path="/bodyguard" element={<Bodyguard />} />

        <Route path="/car-crusher" element={<CarCrusher />} />

        <Route path="/safehouse" element={<Safehouse />} />

        <Route path="/detective-agency" element={<DetectiveAgency />} />

        <Route path="/gicredit/history" element={<GICreditHistory />} />

        <Route path="/mysterybox" element={<MysteryBox />} />

        <Route path="/story" element={<Story />} />

        <Route path="/weekly" element={<Story />} />

        <Route path="/referral" element={<Referral />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <RouteReadyMarker />
    </Suspense>
  </RouteErrorBoundary>
);

export default AppRouter;
